import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router';
import qs from 'query-string';
import { useDispatch, useSelector } from 'react-redux';

import {
    useAddIndirectSubscriptionMutation,
    useCreateSubscriptionMutation,
    useGetInvestmentFundsQuery,
    useGetShareCategoriesQuery,
} from 'api/main';
import { userSelectors, tunnelActions, tunnelSelectors } from 'reducers';
import { useSmallScreen, useMatchAtLeastOneRole, useDistributorShareCategories } from 'utils/hooks';
import { conventionStatus, RoleNames, queryStringifyOptions, entityKey } from 'utils/constants';
import { getEntityIdFromIRI } from 'utils/helpers';
import { texts } from 'utils/languages/fr';

import { TunnelStep } from '../common';
import { LargeChoice, MobileChoice } from './common-components/Choice';
import SubscriptionStartDialog from './common-components/SubscriptionStartDialog';
import { Loader, ShareCategoryDialog, SubmitButton } from 'components';
import { Grid, Typography } from '@mui/material';

const FundSelect = props => {
    const { setFundSubmitted, fundSubmitted, isIndirect, hasFinishedSubscription } = props;
    const isMobile = useSmallScreen('sm');
    const location = useLocation();
    const dispatch = useDispatch();
    const params = qs.parse(location.search, queryStringifyOptions);
    const userId = params?.userId;
    const selectedShareCategory = useSelector(userSelectors.getShareCategory);

    const userIsDistributor = useMatchAtLeastOneRole([
        RoleNames.ADMIN_CIF,
        RoleNames.COMMERCIALISATEUR,
        RoleNames.PSI,
    ]);
    const userIsInvestor = useMatchAtLeastOneRole([
        RoleNames.PP_PROFESSIONNEL,
        RoleNames.PM_PROFESSIONNEL,
    ]);

    const { data: investmentFunds = [], isFetching: fundsLoader } = useGetInvestmentFundsQuery({
        visible: 1,
        enabled: 1,
    });
    const { data: shareCategories = [], isLoading: categoriesLoader } = useGetShareCategoriesQuery(
        {},
        { skip: !userIsDistributor }
    );
    const [
        createSubscription,
        { isLoading: createSubscriptionLoading, isSuccess: createSuccessful, reset },
    ] = useCreateSubscriptionMutation();
    const [addIndirectSubscription, { isLoading: addSubscriptionLoading }] =
        useAddIndirectSubscriptionMutation();

    const { id: fundId } = useSelector(tunnelSelectors.getCurrentInvestmentFund);
    const currentSubscription = useSelector(userSelectors.getCurrentSubscription);
    const [shareCategoryDialogOpen, setShareCategoryDialogOpen] = useState(false);
    const [subscriptionStartDialogOpen, setSubscriptionStartDialogOpen] = useState(false);
    const [distributorCategories] = useDistributorShareCategories(fundId);

    const handleChange = useCallback(
        value => () => {
            dispatch(tunnelActions.setCurrentInvestmentFund(value));
        },
        [dispatch]
    );

    const handleConfirmShareCategory = useCallback(() => {
        setShareCategoryDialogOpen(false);
        if (isIndirect) {
            setSubscriptionStartDialogOpen(true);
        } else {
            setFundSubmitted(true);
        }
    }, [setFundSubmitted, isIndirect]);

    const handleStartSubscription = useCallback(() => {
        setSubscriptionStartDialogOpen(false);
        if (userId) {
            let fundShareCategories = shareCategories.filter(
                cat => getEntityIdFromIRI(cat.investmentFund, entityKey.INVESTMENT_FUND) === +fundId
            );
            // adding a new subscription to an existing subscriber
            addIndirectSubscription({
                userId,
                categoryId: selectedShareCategory?.id || fundShareCategories?.[0]?.id,
            });
        } else {
            // creating an indirect subscription with a new subscriber account - normal tunnel flow
            setFundSubmitted(true);
        }
    }, [
        userId,
        addIndirectSubscription,
        fundId,
        setFundSubmitted,
        shareCategories,
        selectedShareCategory,
    ]);

    const handleSubmit = useCallback(() => {
        const fund = investmentFunds.find(f => f.id === fundId);

        if (userIsDistributor) {
            if (distributorCategories.length > 1 || !userId) {
                // distributor either has access to both public and clean shares OR multiple share categories match with the fund
                // distributor needs to select the category from the dialog OR
                // if only one category matches it will automatically open the subscription start dialog from the share category dialog confirm callback
                setShareCategoryDialogOpen(true);
                setFundSubmitted();
            } else if (distributorCategories.length === 1 && userId) {
                // adding another subscription to an existing investor when only one share category matches
                addIndirectSubscription({ userId, categoryId: distributorCategories?.[0]?.id });
            } else {
                // this should never be the case but we leave it here for now
                setFundSubmitted(true);
            }
        } else if (hasFinishedSubscription && userIsInvestor) {
            // create another subscription as investor
            const fundShareCategories = shareCategories.filter(
                cat => getEntityIdFromIRI(cat.investmentFund, entityKey.INVESTMENT_FUND) === fund.id
            );
            let filteredCategories = [];
            if (currentSubscription.distributorCompany) {
                // investor has a distributor
                filteredCategories = fundShareCategories.filter(cat =>
                    cat.private
                        ? currentSubscription?.distributorCompany?.privateCategoryCommercialization
                        : currentSubscription?.distributorCompany?.publicCategoryCommercialization
                );
            } else {
                // direct investor(no distributor) non-initial subscription
                // only public share categories
                filteredCategories = fundShareCategories.filter(cat => !cat.private);
            }
            createSubscription({ shareCategoryId: filteredCategories?.[0]?.id });
        } else {
            // first direct subscription flow
            setFundSubmitted(true);
        }
    }, [
        addIndirectSubscription,
        createSubscription,
        currentSubscription,
        distributorCategories,
        fundId,
        hasFinishedSubscription,
        investmentFunds,
        setFundSubmitted,
        shareCategories,
        userId,
        userIsDistributor,
        userIsInvestor,
    ]);

    const filteredFundList = useMemo(
        () =>
            investmentFunds.filter(f => {
                return userIsDistributor
                    ? currentSubscription?.distributorCompany?.conventions.find(
                          c =>
                              +c.investmentFund?.id === f.id &&
                              c.status === conventionStatus.FULLY_SIGNED
                      )
                    : !f.private;
            }),
        [investmentFunds, currentSubscription, userIsDistributor]
    );

    useEffect(() => {
        if (createSuccessful) {
            reset();
            setFundSubmitted(true);
        }
    }, [createSuccessful, setFundSubmitted, reset]);

    if (fundSubmitted) return null;

    return (
        <TunnelStep
            height="100%"
            overflow="auto"
            maxWidth={1010}
            title={texts.BIENVENUE}
            subtitle={' '}
        >
            <Loader loading={fundsLoader || categoriesLoader}>
                {filteredFundList?.length ? (
                    <Grid
                        container
                        justifyContent="center"
                        sx={{ maxWidth: '1010px', width: '100%', margin: '0 auto 30px' }}
                        gap={4}
                    >
                        {filteredFundList.map(fund =>
                            isMobile ? (
                                <MobileChoice
                                    key={fund.id}
                                    label={`${fund.type} ${fund.name}`}
                                    handleChange={handleChange(fund)}
                                    selected={fundId === fund.id}
                                    selectType="fund"
                                    backgroundImage={fund.image.url}
                                />
                            ) : (
                                <LargeChoice
                                    key={fund.id}
                                    label={`${fund.type} ${fund.name}`}
                                    handleChange={handleChange(fund)}
                                    selected={fundId === fund.id}
                                    selectType="fund"
                                    backgroundImage={fund.image.url}
                                />
                            )
                        )}
                    </Grid>
                ) : (
                    <>
                        <Typography align="center">
                            {texts.NO_CONVENTIONS_FOR_AVAILABLE_FUNDS}
                        </Typography>
                        <Typography align="center">{texts.NO_AVAILABLE_FUNDS}</Typography>
                    </>
                )}
            </Loader>
            <SubmitButton
                text={texts.SUIVANT}
                disabled={!fundId}
                loading={addSubscriptionLoading || createSubscriptionLoading}
                onClick={handleSubmit}
            />
            <ShareCategoryDialog
                open={shareCategoryDialogOpen}
                onClose={() => setShareCategoryDialogOpen(false)}
                onConfirm={handleConfirmShareCategory}
            />
            <SubscriptionStartDialog
                open={subscriptionStartDialogOpen}
                handleConfirm={handleStartSubscription}
            />
        </TunnelStep>
    );
};

FundSelect.propTypes = {
    setFundSubmitted: PropTypes.func,
    fundSubmitted: PropTypes.bool,
    isIndirect: PropTypes.bool,
    hasFinishedSubscription: PropTypes.bool,
};

export default FundSelect;
